import { useCallback, useEffect, useState } from "react";
import { sendWidgetChat } from "@/api/widgetChat";
import type { FeedMessage } from "@/types";
import {
  clearLiveState,
  ensureAuthors,
  loadLiveState,
  saveLiveState,
  type LiveChatState,
} from "@/utils/liveChat";
import { getSessionId, loadDialogue, saveDialogue } from "@/utils/session";

export function useLiveChat() {
  const [state, setState] = useState<LiveChatState>(() => loadLiveState());
  const [ready, setReady] = useState(
    () => Boolean(state.userAuthor && state.assistantAuthor)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scrollTick, setScrollTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    ensureAuthors(loadLiveState()).then((next) => {
      if (cancelled) return;
      const dialogue = loadDialogue();
      const merged = dialogue.length ? { ...next, dialogue } : next;
      setState(merged);
      saveLiveState(merged);
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const persist = useCallback((next: LiveChatState) => {
    setState(next);
    saveLiveState(next);
    saveDialogue(next.dialogue);
  }, []);

  const ask = useCallback(
    async (text: string) => {
      const question = text.trim();
      if (!question || loading || !ready) return;

      setError(null);
      setLoading(true);

      const authors = await ensureAuthors(state);
      let nextId = state.nextId;
      const now = new Date().toISOString();
      const baseMessages = state.messages;

      const userMsg: FeedMessage = {
        id: String(nextId),
        date: now,
        author: authors.userAuthor,
        text: question,
        author_kind: "user",
        is_live: true,
      };
      nextId -= 1;

      const typingMsg: FeedMessage = {
        id: String(nextId),
        date: now,
        author: authors.assistantAuthor,
        text: "…",
        author_kind: "bot",
        reply_to: userMsg.id,
        is_live: true,
        is_typing: true,
      };
      nextId -= 1;

      persist({
        ...authors,
        nextId,
        messages: [...baseMessages, userMsg, typingMsg],
        dialogue: state.dialogue,
      });
      setScrollTick((t) => t + 1);

      try {
        const res = await sendWidgetChat({
          message: question,
          history: state.dialogue,
          sessionId: getSessionId(),
        });

        const dialogue = res.dialogue?.length
          ? res.dialogue
          : [
              ...state.dialogue,
              { role: "user" as const, content: question },
              { role: "assistant" as const, content: res.answer },
            ];

        const answerMsg: FeedMessage = {
          id: String(nextId),
          date: new Date().toISOString(),
          author: authors.assistantAuthor,
          text: res.answer,
          author_kind: "bot",
          reply_to: userMsg.id,
          is_live: true,
        };

        persist({
          ...authors,
          nextId: nextId - 1,
          dialogue,
          messages: [...baseMessages, userMsg, answerMsg],
        });
        setScrollTick((t) => t + 1);
      } catch (err) {
        persist({
          ...authors,
          nextId: state.nextId,
          messages: [...baseMessages, userMsg],
          dialogue: state.dialogue,
        });
        setError(
          err instanceof Error ? err.message : "Не удалось получить ответ"
        );
      } finally {
        setLoading(false);
      }
    },
    [loading, ready, state, persist]
  );

  const clear = useCallback(async () => {
    clearLiveState();
    saveDialogue([]);
    const fresh = await ensureAuthors({
      userAuthor: "",
      assistantAuthor: "",
      messages: [],
      dialogue: [],
      nextId: -1,
    });
    persist(fresh);
    setError(null);
  }, [persist]);

  const removeMessage = useCallback(
    (id: string) => {
      const key = String(id);
      const nextMessages = state.messages.filter((m) => String(m.id) !== key);
      persist({ ...state, messages: nextMessages });
    },
    [state, persist]
  );

  return {
    liveMessages: state.messages,
    loading,
    error,
    ask,
    clear,
    removeMessage,
    scrollTick,
    userAuthor: state.userAuthor,
  };
}
